import {
  InfiniteQueryObserver,
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useQueryClient,
} from '@tanstack/react-query';
import { QueryKeys } from 'src/hooks/QueryKeys';
import { fetch } from '../../api/fetchWrapper/fetch';
import { API_URL } from 'src/api';
import { IProductPrice } from '../../types/productPricing.types';
import { SnackbarStatus, useSnackbar } from '@weareopenr/component-library';
import parseQueryParamsFromRelativeUrlPath from '../../helpers/parseQueryParams/parseQueryParamsFromRelativePath';
import { useEffect, useMemo } from 'react';
import { PRICE_SCHEME_HEADER } from '../../api/constants';

export interface IPriceManagementResponse {
  data: IProductPrice[];
  paging: {
    next: string | null;
  };
}

const getProductPrices = async (
  priceSchemeId: string,
  limit: number,
  afterId?: string,
  searchParam?: string,
  menusParam?: string[],
  ranges?: string[],
  pricingCategories?: string[],
  missingDefaultPrice?: boolean,
): Promise<IPriceManagementResponse> => {
  const searchParams = new URLSearchParams();
  searchParams.append('limit', limit.toString());
  if (afterId) {
    searchParams.append('after_id', afterId);
  }
  if (searchParam) {
    searchParams.append('product_name', searchParam);
  }
  if (menusParam && menusParam.length > 0) {
    searchParams.append('menus', menusParam.join(','));
  }
  if (ranges && ranges.length > 0) {
    searchParams.append('ranges', ranges.join(','));
  }
  if (pricingCategories && pricingCategories.length > 0) {
    searchParams.append('pricing_categories', pricingCategories.join(','));
  }
  if (missingDefaultPrice) {
    searchParams.append('missing_default_price', 'true');
  }

  const res = await fetch(`${API_URL}/pricing/product-prices?${searchParams.toString()}`, {
    headers: { [PRICE_SCHEME_HEADER]: priceSchemeId },
  });
  return await res.json();
};

const useProductPrices = (
  priceSchemeId: string,
  limit: number = 50,
  options?: UseInfiniteQueryOptions<IPriceManagementResponse> & {
    searchParam?: string;
    menus?: string[];
    ranges?: string[];
    pricingCategories?: string[];
    missingDefaultPrice?: boolean;
  },
) => {
  const { showSnackbar } = useSnackbar();
  const queryKey = useMemo(
    () => [
      QueryKeys.productPrices,
      limit,
      priceSchemeId,
      options?.searchParam,
      options?.menus,
      options?.ranges,
      options?.pricingCategories,
      options?.missingDefaultPrice,
    ],
    [
      limit,
      priceSchemeId,
      options?.searchParam,
      options?.menus,
      options?.ranges,
      options?.pricingCategories,
      options?.missingDefaultPrice,
    ],
  );

  const query = useInfiniteQuery<IPriceManagementResponse>({
    queryKey,
    queryFn: async ({ pageParam = '' }) =>
      getProductPrices(
        priceSchemeId,
        limit,
        pageParam,
        options?.searchParam,
        options?.menus,
        options?.ranges,
        options?.pricingCategories,
        options?.missingDefaultPrice,
      ),
    getNextPageParam: (res) => {
      if (res.paging.next === null) {
        return null;
      }
      const searchParams = parseQueryParamsFromRelativeUrlPath(res.paging.next);
      return searchParams.get('after_id');
    },
    refetchOnWindowFocus: false,
    onError: () => {
      showSnackbar({ status: SnackbarStatus.ERROR, message: 'Unable to fetch product prices' });
    },
    ...options,
  });

  return { ...query, queryKey };
};

export default useProductPrices;

export const useSetDefaults = (onNewProducts: (products: IProductPrice[]) => void, queryKey: QueryKey) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    const observer = new InfiniteQueryObserver<IPriceManagementResponse>(queryClient, { queryKey });

    const unsubscribe = observer.subscribe((result) => {
      if (result.isSuccess && result.fetchStatus === 'idle') {
        const products = result.data?.pages.flatMap((page) => page.data.map((product) => product));

        onNewProducts(products);
      }
    });

    return unsubscribe;
  }, [queryClient, queryKey, onNewProducts]);
};
